// import { useQuery } from '@tanstack/react-query';
// import api from '../../domain/api';
// import toast from 'react-hot-toast';
// import { URI } from '../../domain/constant';

// const GalleryList = ({ onEdit }) => {
//   const { data: galleryItems, isLoading } = useQuery({
//     queryKey: ['gallery'],
//     queryFn: async () => api.get('/gallery').then((res) => res.data),
//   });

//   const handleDelete = async (id) => {
//     if (!window.confirm('Are you sure you want to delete this gallery?'))
//       return;
//     try {
//       await api.delete(`/gallery/${id}`);
//       toast.success('Gallery deleted');
//       // refetch();
//     } catch (err) {
//       toast.error('Failed to delete gallery');
//     }
//   };

//   return (
//     <div className='gallery-list-container'>
//       <h2 className='mb-3'>All Galleries</h2>
//       {isLoading ? (
//         <div>Loading...</div>
//       ) : (
//         <table className='table table-bordered'>
//           <thead>
//             <tr>
//               <th>Description</th>
//               <th>Image</th>
//               <th>Author</th>
//               <th>Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {galleryItems.length === 0 && (
//               <tr>
//                 <td colSpan={4}>No galleries found.</td>
//               </tr>
//             )}
//             {galleryItems.map((g) => (
//               <tr key={g._id}>
//                 <td>{g.description}</td>
//                 <td>
//                   {Array.isArray(g.image) ? (
//                     g.image.map((img, idx) => (
//                       <img
//                         key={idx}
//                         src={URI + img}
//                         alt='gallery'
//                         style={{ width: 60, marginRight: 4 }}
//                       />
//                     ))
//                   ) : (
//                     <img src={g.image} alt='gallery' style={{ width: 60 }} />
//                   )}
//                 </td>
//                 <td>{g.author}</td>
//                 <td>
//                   <button
//                     className='btn btn-sm btn-warning me-2'
//                     onClick={() => onEdit(g)}
//                   >
//                     Edit
//                   </button>
//                   <button
//                     className='btn btn-sm btn-danger'
//                     onClick={() => handleDelete(g._id)}
//                   >
//                     Delete
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       )}
//     </div>
//   );
// };

// export default GalleryList;

'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../../domain/api';
import toast from 'react-hot-toast';
import { URI } from '../../domain/constant';
import EnhancedEditGalleryForm from './edit-gallery-form';
import ConfirmModal from '../../components/common/ConfirmModal';

const EnhancedGalleryList = ({ onEdit }) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedImages, setSelectedImages] = useState([]);
  const [editingGallery, setEditingGallery] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [galleryToDelete, setGalleryToDelete] = useState(null);
  const queryClient = useQueryClient();

  const { data: galleryItems, isLoading } = useQuery({
    queryKey: ['gallery'],
    queryFn: async () => api.get('/gallery').then((res) => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/gallery/${id}`),
    onSuccess: () => {
      toast.success('Gallery entry deleted successfully');
      setShowDeleteModal(false);
      setGalleryToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['gallery'] });
    },
    onError: () => {
      toast.error('Failed to delete gallery entry');
      setShowDeleteModal(false);
      setGalleryToDelete(null);
    },
  });

  const handleDeleteClick = (galleryId) => {
    setGalleryToDelete(galleryId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (galleryToDelete) {
      deleteMutation.mutate(galleryToDelete);
    }
  };

  const handleEdit = (gallery) => {
    setEditingGallery(gallery);
  };

  const handleCloseEdit = () => {
    setEditingGallery(null);
  };

  const handleImageClick = (images, index) => {
    setSelectedImages({ images, currentIndex: index });
  };

  const closeImageModal = () => {
    setSelectedImages([]);
  };

  const nextImage = () => {
    if (selectedImages.currentIndex < selectedImages.images.length - 1) {
      setSelectedImages((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
      }));
    }
  };

  const prevImage = () => {
    if (selectedImages.currentIndex > 0) {
      setSelectedImages((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex - 1,
      }));
    }
  };

  if (isLoading) {
    return (
      <div className='enhanced-gallery-loading'>
        <div className='enhanced-loading-spinner'></div>
        <p>Loading gallery...</p>
      </div>
    );
  }

  return (
    <>
      <div className='enhanced-gallery-list-container'>
        {/* Header */}
        <div className='enhanced-gallery-header'>
          <div className='enhanced-header-left'>
            <div className='enhanced-header-icon'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
            <div>
              <h2 className='enhanced-gallery-title'>Gallery Collection</h2>
              <p className='enhanced-gallery-subtitle'>
                {galleryItems?.length || 0} entries found
              </p>
            </div>
          </div>

          <div className='enhanced-header-actions'>
            <div className='enhanced-view-toggle'>
              <button
                className={`enhanced-view-btn ${
                  viewMode === 'grid' ? 'active' : ''
                }`}
                onClick={() => setViewMode('grid')}
              >
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                  <rect
                    x='3'
                    y='3'
                    width='7'
                    height='7'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <rect
                    x='14'
                    y='3'
                    width='7'
                    height='7'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <rect
                    x='14'
                    y='14'
                    width='7'
                    height='7'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <rect
                    x='3'
                    y='14'
                    width='7'
                    height='7'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
              <button
                className={`enhanced-view-btn ${
                  viewMode === 'list' ? 'active' : ''
                }`}
                onClick={() => setViewMode('list')}
              >
                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                  <line
                    x1='8'
                    y1='6'
                    x2='21'
                    y2='6'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <line
                    x1='8'
                    y1='12'
                    x2='21'
                    y2='12'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <line
                    x1='8'
                    y1='18'
                    x2='21'
                    y2='18'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <line
                    x1='3'
                    y1='6'
                    x2='3.01'
                    y2='6'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <line
                    x1='3'
                    y1='12'
                    x2='3.01'
                    y2='12'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <line
                    x1='3'
                    y1='18'
                    x2='3.01'
                    y2='18'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='enhanced-gallery-content'>
          {!galleryItems || galleryItems.length === 0 ? (
            <div className='enhanced-empty-state'>
              <div className='enhanced-empty-icon'>
                <svg width='64' height='64' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <h3>No Gallery Entries Found</h3>
              <p>
                Start by adding your first gallery entry to showcase your
                moments.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className='enhanced-gallery-grid'>
              {galleryItems.map((gallery) => (
                <div key={gallery._id} className='enhanced-gallery-card'>
                  <div className='enhanced-card-images'>
                    {Array.isArray(gallery.image) &&
                    gallery.image.length > 0 ? (
                      <div className='enhanced-image-stack'>
                        <img
                          src={URI + gallery.image[0] || '/placeholder.svg'}
                          alt='Gallery'
                          className='enhanced-main-image'
                          onClick={() => handleImageClick(gallery.image, 0)}
                        />
                        {gallery.image.length > 1 && (
                          <div className='enhanced-image-count'>
                            +{gallery.image.length - 1}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='enhanced-no-image'>
                        <svg
                          width='32'
                          height='32'
                          viewBox='0 0 24 24'
                          fill='none'
                        >
                          <path
                            d='M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className='enhanced-card-content'>
                    <p className='enhanced-card-description'>
                      {gallery.description}
                    </p>
                    <div className='enhanced-card-meta'>
                      <span className='enhanced-card-author'>
                        <svg
                          width='14'
                          height='14'
                          viewBox='0 0 24 24'
                          fill='none'
                        >
                          <path
                            d='M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                        {gallery.author}
                      </span>
                    </div>
                  </div>

                  <div className='enhanced-card-actions'>
                    <button
                      className='enhanced-action-btn edit'
                      onClick={() => handleEdit(gallery)}
                      title='Edit gallery'
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                      >
                        <path
                          d='M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </button>
                    <button
                      className='enhanced-action-btn delete'
                      onClick={() => handleDeleteClick(gallery._id)}
                      title='Delete gallery'
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                      >
                        <path
                          d='M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19ZM10 11V17M14 11V17'
                          stroke='currentColor'
                          strokeWidth='2'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='enhanced-gallery-table'>
              <div className='enhanced-table-header'>
                <div className='enhanced-table-cell'>Description</div>
                <div className='enhanced-table-cell'>Images</div>
                <div className='enhanced-table-cell'>Author</div>
                <div className='enhanced-table-cell'>Actions</div>
              </div>
              {galleryItems.map((gallery) => (
                <div key={gallery._id} className='enhanced-table-row'>
                  <div className='enhanced-table-cell'>
                    <p className='enhanced-table-description'>
                      {gallery.description}
                    </p>
                  </div>
                  <div className='enhanced-table-cell'>
                    <div className='enhanced-table-images'>
                      {Array.isArray(gallery.image) &&
                      gallery.image.length > 0 ? (
                        gallery.image
                          .slice(0, 3)
                          .map((img, idx) => (
                            <img
                              key={idx}
                              src={URI + img || '/placeholder.svg'}
                              alt='Gallery'
                              className='enhanced-table-thumb'
                              onClick={() =>
                                handleImageClick(gallery.image, idx)
                              }
                            />
                          ))
                      ) : (
                        <div className='enhanced-no-image-small'>No images</div>
                      )}
                      {gallery.image && gallery.image.length > 3 && (
                        <div className='enhanced-more-images'>
                          +{gallery.image.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='enhanced-table-cell'>
                    <span className='enhanced-table-author'>
                      {gallery.author}
                    </span>
                  </div>
                  <div className='enhanced-table-cell'>
                    <div className='enhanced-table-actions'>
                      <button
                        className='enhanced-action-btn edit small'
                        onClick={() => handleEdit(gallery)}
                      >
                        Edit
                      </button>
                      <button
                        className='enhanced-action-btn delete small'
                        onClick={() => handleDeleteClick(gallery._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image Modal */}
        {selectedImages.images && (
          <div className='enhanced-image-modal' onClick={closeImageModal}>
            <div
              className='enhanced-modal-content'
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className='enhanced-modal-close'
                onClick={closeImageModal}
              >
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                  <path
                    d='M18 6L6 18M6 6L18 18'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>

              <img
                src={
                  URI + selectedImages.images[selectedImages.currentIndex] ||
                  '/placeholder.svg'
                }
                alt='Gallery'
                className='enhanced-modal-image'
              />

              {selectedImages.images.length > 1 && (
                <>
                  <button
                    className='enhanced-modal-nav prev'
                    onClick={prevImage}
                    disabled={selectedImages.currentIndex === 0}
                  >
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path
                        d='M15 18L9 12L15 6'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <button
                    className='enhanced-modal-nav next'
                    onClick={nextImage}
                    disabled={
                      selectedImages.currentIndex ===
                      selectedImages.images.length - 1
                    }
                  >
                    <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                      <path
                        d='M9 18L15 12L9 6'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </button>
                  <div className='enhanced-modal-counter'>
                    {selectedImages.currentIndex + 1} /{' '}
                    {selectedImages.images.length}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingGallery && (
        <EnhancedEditGalleryForm
          galleryData={editingGallery}
          onClose={handleCloseEdit}
        />
      )}

      {/* Confirm Delete Modal */}
      {showDeleteModal && (
        <ConfirmModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          message='Are you sure you want to delete this gallery entry?'
        />
      )}

      <style jsx>{`
        .enhanced-gallery-list-container {
          background: #f8fafc;
          min-height: 100vh;
          padding: 20px;
        }

        .enhanced-gallery-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          color: #6b7280;
        }

        .enhanced-loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e7eb;
          border-top: 3px solid #dc2626;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        .enhanced-gallery-header {
          background: linear-gradient(
            135deg,
            #1f2937 0%,
            #374151 30%,
            #dc2626 70%,
            #b91c1c 100%
          );
          color: white;
          padding: 30px;
          border-radius: 20px;
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }

        .enhanced-header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .enhanced-header-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .enhanced-gallery-title {
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 5px 0;
        }

        .enhanced-gallery-subtitle {
          font-size: 16px;
          opacity: 0.9;
          margin: 0;
        }

        .enhanced-header-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .enhanced-view-toggle {
          display: flex;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 4px;
        }

        .enhanced-view-btn {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .enhanced-view-btn.active,
        .enhanced-view-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .enhanced-gallery-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .enhanced-empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
        }

        .enhanced-empty-icon {
          color: #d1d5db;
          margin-bottom: 20px;
        }

        .enhanced-empty-state h3 {
          font-size: 24px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 10px 0;
        }

        .enhanced-empty-state p {
          font-size: 16px;
          margin: 0;
        }

        .enhanced-gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 25px;
        }

        .enhanced-gallery-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border: 1px solid #f1f5f9;
        }

        .enhanced-gallery-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
        }

        .enhanced-card-images {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .enhanced-image-stack {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .enhanced-main-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          cursor: pointer;
          transition: transform 0.3s ease;
        }

        .enhanced-main-image:hover {
          transform: scale(1.05);
        }

        .enhanced-image-count {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .enhanced-no-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
          color: #9ca3af;
        }

        .enhanced-card-content {
          padding: 20px;
        }

        .enhanced-card-description {
          font-size: 15px;
          color: #374151;
          margin: 0 0 12px 0;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .enhanced-card-meta {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .enhanced-card-author {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #6b7280;
        }

        .enhanced-card-actions {
          padding: 0 20px 20px;
          display: flex;
          gap: 10px;
        }

        .enhanced-action-btn {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .enhanced-action-btn.edit {
          background: #f0f9ff;
          color: #0369a1;
          border: 1px solid #e0f2fe;
        }

        .enhanced-action-btn.edit:hover {
          background: #0369a1;
          color: white;
        }

        .enhanced-action-btn.delete {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .enhanced-action-btn.delete:hover {
          background: #dc2626;
          color: white;
        }

        .enhanced-action-btn.small {
          padding: 6px 12px;
          font-size: 12px;
        }

        .enhanced-gallery-table {
          display: flex;
          flex-direction: column;
          gap: 1px;
          background: #f1f5f9;
          border-radius: 12px;
          overflow: hidden;
        }

        .enhanced-table-header {
          display: grid;
          grid-template-columns: 2fr 1.5fr 1fr 1fr;
          background: #f8fafc;
          font-weight: 600;
          color: #374151;
        }

        .enhanced-table-row {
          display: grid;
          grid-template-columns: 2fr 1.5fr 1fr 1fr;
          background: white;
          transition: background 0.2s ease;
        }

        .enhanced-table-row:hover {
          background: #f8fafc;
        }

        .enhanced-table-cell {
          padding: 16px;
          display: flex;
          align-items: center;
        }

        .enhanced-table-description {
          margin: 0;
          font-size: 14px;
          color: #374151;
          line-height: 1.4;
        }

        .enhanced-table-images {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .enhanced-table-thumb {
          width: 40px;
          height: 40px;
          object-fit: cover;
          border-radius: 6px;
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .enhanced-table-thumb:hover {
          transform: scale(1.1);
        }

        .enhanced-no-image-small {
          font-size: 12px;
          color: #9ca3af;
          font-style: italic;
        }

        .enhanced-more-images {
          background: #f3f4f6;
          color: #6b7280;
          font-size: 11px;
          padding: 4px 6px;
          border-radius: 4px;
          font-weight: 500;
        }

        .enhanced-table-author {
          font-size: 14px;
          color: #6b7280;
        }

        .enhanced-table-actions {
          display: flex;
          gap: 8px;
        }

        .enhanced-image-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .enhanced-modal-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
        }

        .enhanced-modal-image {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: 8px;
        }

        .enhanced-modal-close {
          position: absolute;
          top: -50px;
          right: 0;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }

        .enhanced-modal-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .enhanced-modal-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .enhanced-modal-nav:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.2);
        }

        .enhanced-modal-nav:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .enhanced-modal-nav.prev {
          left: -70px;
        }

        .enhanced-modal-nav.next {
          right: -70px;
        }

        .enhanced-modal-counter {
          position: absolute;
          bottom: -50px;
          left: 50%;
          transform: translateX(-50%);
          color: white;
          background: rgba(0, 0, 0, 0.5);
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .enhanced-gallery-list-container {
            padding: 10px;
          }

          .enhanced-gallery-header {
            padding: 20px;
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }

          .enhanced-gallery-content {
            padding: 20px;
          }

          .enhanced-gallery-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .enhanced-table-header,
          .enhanced-table-row {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .enhanced-table-cell {
            padding: 12px;
            border-bottom: 1px solid #f1f5f9;
          }

          .enhanced-modal-nav.prev {
            left: 10px;
          }

          .enhanced-modal-nav.next {
            right: 10px;
          }
        }
      `}</style>
    </>
  );
};

export default EnhancedGalleryList;
